import React from "react";
import {
  useLocation,
  useNavigate,
  matchPath,
  useParams,
} from "react-router-dom";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  useMediaQuery,
  Typography,
  Stack,
  Theme,
  alpha,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Adjust as AdjustIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";

import { navigationSections, NavigationSection, homeNavigationSection } from "../config/navigationConfig";
import { filterNavigationItems } from "../config/navAccess";
import { useCapabilities } from "../../hooks/useCapabilities";
import { useNavigation } from "../../hooks/useNavigation";
import { getClientTheme } from "../../clientConfig";

const clientTheme = getClientTheme();

const sidebarLayout = clientTheme.layout;
const hasDarkSidebar = !!sidebarLayout?.sidebarBackground;
const navTextActive   = sidebarLayout?.sidebarTextPrimary   ?? undefined;
const navTextInactive = sidebarLayout?.sidebarTextSecondary ?? undefined;
const activeItemBg   = sidebarLayout?.sidebarActiveItemBg  ?? null;
const logoBoxBg      = sidebarLayout?.sidebarLogoBoxBg     ?? null;
const logoBoxText    = sidebarLayout?.sidebarLogoBoxText   ?? null;

const DRAWER_WIDTH = 260;

interface SideNavProps {
  onClose: () => void;
  open: boolean;
}

export const SideNav: React.FC<SideNavProps> = ({ onClose, open }) => {

  const location = useLocation();
  const navigate = useNavigate();
  const { portfolioId, portfolioName, projectId, projectName } = useParams();
  const isMdUp = useMediaQuery("(min-width:900px)");
  const { canRoute } = useCapabilities();
  const { goBack } = useNavigation();

  // Replace :projectId with actual ID in paths
  const resolvePath = (path: string) =>
    projectId ? path.replace(":projectId", projectId) : path;

  // Flatten nested items for parent lookup
  const flattenItems = (items: any[]): any[] =>
    items.flatMap((item) => [
      item,
      ...(item.item ? flattenItems(item.item) : []),
    ]);

  const allItems = flattenItems(navigationSections.flatMap((section: any) => section.items));

  const activeParent = allItems.find(
    (item) =>
      item.item &&
      item.item.length > 0 &&
      matchPath({ path: resolvePath(item.path), end: false }, location.pathname)
  );

  const isInChildSection = !!activeParent && activeParent.isStandAlone;
  const [openItems, setOpenItems] = React.useState<Record<string, boolean>>({});

  const toggleItem = (key: string) => {
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Filter out hidden items
  const filterVisible = (items: any[]) =>
    items.filter((item) => !item.isHiddenInNavigation);

  // 🧩 Recursive render function
  const renderNavItems = (items: any[], level = 0) => (
    <List disablePadding>
      {filterVisible(items).map((item) => {
        const path = resolvePath(item.path);
        const hasChildren = !!item.items;

        const isParentActive = !!matchPath(
          { path, end: false },
          location.pathname
        );

        const isOpen = openItems[item.label] || isParentActive;

        const isActive = !!matchPath({ path, end: false }, location.pathname);

        return (
          <React.Fragment key={item.label}>
            <Box
              onMouseEnter={() => {
                if (hasChildren) {
                  setOpenItems((prev) => ({ ...prev, [item.label]: true }));
                }
              }}
              onMouseLeave={() => {
                if (hasChildren) {
                  setOpenItems((prev) => ({ ...prev, [item.label]: false }));
                }
              }}
            >
              <ListItemButton
                sx={{
                  pr: 1,
                  pl: 1 + level * 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: isActive
                    ? activeItemBg
                      ? activeItemBg
                      : hasDarkSidebar
                        ? "rgba(255, 255, 255, 0.12)"
                        : (theme: Theme) => alpha(theme.palette.primary.main, 0.15)
                    : "transparent",
                  "&:hover": {
                    bgcolor: isActive
                      ? (activeItemBg ?? (hasDarkSidebar ? "rgba(255, 255, 255, 0.12)" : undefined))
                      : hasDarkSidebar ? "rgba(255, 255, 255, 0.08)" : undefined,
                  },
                }}
                onClick={() => {
                  if (item.comingSoon) return;
                  toggleItem(item.label);

                  // Leaf navigation
                  if (isInChildSection) {
                    if (portfolioId && portfolioName) {
                      navigate(path.replace(":portfolioId", portfolioId || "").replace(":portfolioName", portfolioName || "").replace(":projectId", projectId || "").replace(":projectName", projectName || ""), { replace: true });
                    } else {
                      navigate(path.replace(":projectId", projectId || "").replace(":projectName", projectName || ""), { replace: true });
                    }
                  } else {
                    navigate(path);
                  }

                  if (!isMdUp) onClose();
                }}
              >
                {/* ICON */}
                {item.icon && (
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                      color: isActive
                        ? (navTextActive ?? "primary.main")
                        : (navTextInactive ?? "text.secondary"),
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                )}


                {/* TEXT */}
                <ListItemText
                  primary={item.label}
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: 14,
                        fontWeight: isActive && hasDarkSidebar ? 600 : 400,
                        color: isActive
                          ? (navTextActive ?? "primary.main")
                          : (navTextInactive ?? "text.secondary"),
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    },
                  }}
                />

                {item.comingSoon && (
                  <Tooltip title="Coming Soon">
                    <InfoOutlinedIcon fontSize="small" opacity={0.2} />
                  </Tooltip>
                )}

                {/* DROPDOWN ICON */}
                {/* {hasChildren && (
                  <Box sx={{ color: "text.secondary", pl: 1 }}>
                    {isOpen ? "▾" : "▸"}
                  </Box>
                )} */}

              </ListItemButton>

              {/* CHILDREN LIST */}
              {hasChildren && isOpen && (
                <Box
                  sx={{
                    pl: 2,
                    animation: "slideDown 0.2s ease-in-out",
                    "@keyframes slideDown": {
                      from: {
                        opacity: 0,
                        maxHeight: 0,
                        overflow: "hidden",
                      },
                      to: {
                        opacity: 1,
                        maxHeight: "500px",
                        overflow: "visible",
                      },
                    },
                  }}
                >
                  {renderNavItems(item.items, level + 1)}
                </Box>
              )}
            </Box>
          </React.Fragment>
        );
      })}
    </List>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { md: open ? DRAWER_WIDTH : 0 },
        flexShrink: { md: 0 },
        transition: "width 0.3s ease",
      }}
    >
      <Drawer
        variant={isMdUp ? "persistent" : "temporary"}
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            border: "none",
            transition: "width 0.3s ease",
            backgroundColor: sidebarLayout?.sidebarBackground
              ? sidebarLayout.sidebarBackground
              : (theme: Theme) =>
                  !isMdUp ? theme.palette.background.default : "transparent",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1,
                backgroundColor: logoBoxBg
                  ? logoBoxBg
                  : (theme: Theme) => theme.palette.primary.main,
                color: logoBoxText
                  ? logoBoxText
                  : (theme: Theme) => theme.palette.primary.contrastText,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "0.9rem",
                letterSpacing: "0.5px",
              }}
            >
              {logoBoxBg ? "CD" : <AdjustIcon />}
            </Box>
            <Stack spacing={0}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ color: navTextActive ?? "text.primary" }}
              >
                Capital Delivery
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: navTextInactive ?? "text.secondary", fontSize: "0.72rem" }}
              >
                {clientTheme.platformName ?? "Capital Delivery Platform"}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* Navigation content */}
        {/* <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1 }}> */}
        <Box
          sx={{
            mt: 1,
            overflowY: "auto",
            flex: 1,
            gap: 1,
            display: "flex",
            flexDirection: "column",
            px: 2,
          }}
        >
          {isInChildSection && activeParent?.isStandAlone ? (
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <IconButton
                  size="small"
                  onClick={() => goBack()}
                  sx={{ color: navTextInactive ?? "inherit" }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography
                  variant="subtitle2"
                  sx={{ color: navTextInactive ?? "text.secondary" }}
                >
                  {activeParent.label}
                </Typography>
              </Stack>
              {filterNavigationItems(activeParent.item, canRoute).map(
                (section: NavigationSection, idx: number) => (
                  <Box key={idx}>
                    {section.category && (
                      <Typography
                        variant="overline"
                        sx={{ color: navTextInactive ?? "text.secondary" }}
                      >
                        {section.category}
                      </Typography>
                    )}
                    {renderNavItems(section.items)}
                  </Box>
                )
              )}
            </Box>
          ) : (
            filterNavigationItems([...homeNavigationSection, ...navigationSections], canRoute).map((section: NavigationSection, idx: number) => (
              <Box key={idx} sx={{}}>
                {section.smallTitle && (
                  <Typography
                    variant="overline"
                    sx={{ color: navTextInactive ?? "text.secondary" }}
                  >
                    {section.smallTitle}
                  </Typography>
                )}
                {renderNavItems(section.items)}
              </Box>
            ))
          )}
        </Box>
      </Drawer>
    </Box>
  );
};